import React, { useState, useEffect, useMemo, useRef } from "react";
import queries from "../helpers/queries";
import { withUser } from "../Contexts/user";
import { useQuery } from "@apollo/react-hooks";
const moment = require("moment");

const dateTo = moment(Date.now()).subtract(1, "day").format("YYYY-MM-DDTHH:mm:ss.SSSZ");
const dateFrom = moment(Date.now()).subtract(1, "day").format("YYYY-MM-DDTHH:mm:ss.SSSZ");

const SignedPercentage = (user) => {
    const [rankingData, setRankingData] = useState([]);
    const [selectedFilter, setSelectedFilter] = useState("OVERALL");
    const [bgPosY, setBgPosY] = useState(50); // percent

    const rankingCardRef = useRef(null);
    const rankingContentRef = useRef(null);
    const rafIdRef = useRef(null);

    // Determine state name based on user's state
    let stateName = ""
    if (user.user.auth.state.name === "Kuala Lumpur") {
        stateName = "kl"
    } else if (user.user.auth.state.name === "Johor Bahru") {
        stateName = "jb"
    } else if (user.user.auth.state.name === "Melaka") {
        stateName = "melaka"
    } else if (user.user.auth.state.name === "Ipoh") {
        stateName = "ipoh"
    } else if (user.user.auth.state.name === "Penang") {
        stateName = "penang"
    }

    const { data: casesData } = useQuery(
        queries.FETCH_TOP_30,
        {
            fetchPolicy: "network-only",
            variables: {
                dateTo: dateTo,
                dateFrom: dateFrom,                             
                state: selectedFilter === "OVERALL" ? "all" : stateName,
                type: "incentive"
            },
            onCompleted: () => {
                const originalData = casesData.fetchTop30s.data.top30;
                setRankingData(originalData);
            },
        }
    );

    const bgStyle = useMemo(
        () => ({
            backgroundImage: `url('https://venti-fans.com/wp-content/uploads/2026/03/DD_AgentPortal_LondonBG-scaled.jpg')`,
            backgroundSize: "cover",
            backgroundPosition: `center ${bgPosY}%`,
            backgroundRepeat: "no-repeat",
        }),
        [bgPosY]
    );

    useEffect(() => {
        const el = rankingContentRef.current;
        if (!el) return;

        const update = () => {
            rafIdRef.current = null;
            const maxScroll = el.scrollHeight - el.clientHeight;
            const ratio = maxScroll > 0 ? el.scrollTop / maxScroll : 0;
            setBgPosY(10 + ratio * 80); // move from ~top to ~bottom
        };

        const onScroll = () => {
            if (rafIdRef.current != null) return;
            rafIdRef.current = requestAnimationFrame(update);
        };

        // initial sync (in case content already scrolls)
        update();

        el.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            el.removeEventListener("scroll", onScroll);
            if (rafIdRef.current != null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
        };
    }, []);

    const capitalizeWords = str => {
        return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
    };

    const formatName = (member) => {
        if (!member) return "Unknown";
        const nickname = member.nickname && member.nickname !== "-" ? member.nickname : "";
        const name = member.name || "";
        return `${capitalizeWords(nickname)} ${capitalizeWords(name)}`.trim();
    };

    const renderTopThree = () => {
        const top3 = rankingData.slice(0, 3);
        return (
            <div className="top-circles">
                {/* Second Place */}
                <div className="ranking-circle">
                    <div className="circle-container circle-small">
                        <img 
                            src={top3[1]?.member?.image || "https://venti-fans.com/wp-content/uploads/2025/04/DD_SystemIcons-28.png"}
                            alt={top3[1]?.member ? formatName(top3[1].member) : "Second Place"}
                            className="circle-image"
                        />
                        <img 
                            src="https://venti-fans.com/wp-content/uploads/2025/04/DD_SystemIcons-29.png"
                            alt="Silver Medal"
                            className="ranking-medal"
                        />
                    </div>
                    <div className="ranking-name">{top3[1]?.member ? formatName(top3[1].member) : "N/A"}</div>
                    <div className="ranking-percentage">{top3[1]?.commission ? top3[1].commission.toFixed(2) : "0.00"}%</div>
                </div>

                {/* First Place */}
                <div className="ranking-circle">
                    <div className="circle-container circle-large">
                        <img 
                            src={top3[0]?.member?.image || "https://venti-fans.com/wp-content/uploads/2025/04/DD_SystemIcons-28.png"}
                            alt={top3[0]?.member ? formatName(top3[0].member) : "First Place"}
                            className="circle-image"
                        />
                        <img 
                            src="https://venti-fans.com/wp-content/uploads/2025/04/DD_SystemIcons-28.png"
                            alt="Gold Medal"
                            className="ranking-medal"
                        />
                    </div>
                    <div className="ranking-name">{top3[0]?.member ? formatName(top3[0].member) : "N/A"}</div>
                    <div className="ranking-percentage">{top3[0]?.commission ? top3[0].commission.toFixed(2) : "0.00"}%</div>
                </div>

                {/* Third Place */}
                <div className="ranking-circle">
                    <div className="circle-container circle-small">
                        <img 
                            src={top3[2]?.member?.image || "https://venti-fans.com/wp-content/uploads/2025/04/DD_SystemIcons-28.png"}
                            alt={top3[2]?.member ? formatName(top3[2].member) : "Third Place"}
                            className="circle-image"
                        />
                        <img 
                            src="https://venti-fans.com/wp-content/uploads/2025/04/DD_SystemIcons-30.png"
                            alt="Bronze Medal"
                            className="ranking-medal"
                        />
                    </div>
                    <div className="ranking-name">{top3[2]?.member ? formatName(top3[2].member) : "N/A"}</div>
                    <div className="ranking-percentage">{top3[2]?.commission ? top3[2].commission.toFixed(2) : "0.00"}%</div>
                </div>
            </div>
        );
    };

    const renderRankingList = () => {
        const remainingRankings = rankingData.slice(3);
        const rows = [];
        
        // Group items into rows of 4
        for (let i = 0; i < remainingRankings.length; i += 4) {
            const rowItems = remainingRankings.slice(i, i + 4);
            rows.push(rowItems);
        }

        return (
            <div className="ranking-list">
                {rows.map((row, rowIndex) => (
                    <div key={rowIndex} className="ranking-row">
                        {row.map((item, index) => (
                            <div key={index} className="ranking-item">
                                <span className="ranking-position">Top {4 + rowIndex * 4 + index}</span>
                                <span className="ranking-item-name">{formatName(item.member)}</span>
                                <span className="ranking-item-percentage">{item.commission.toFixed(2)}%</span>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="">
            <div className="flex flex-wrap rounded-2xl w-full overflow-hidden my-10 lg:my-0 lg:p-8">
                <div
                    className="w-full ranking-card"
                    ref={rankingCardRef}
                    style={bgStyle}
                >
                    <div className="ranking-header">
                        <h1 className="ranking-title">TRAVEL INCENTIVE PERCENTAGE</h1>
                    </div>
                    <div className="ranking-card-content" ref={rankingContentRef}>
                        <div className="ranking-container">
                            {renderTopThree()}
                            {renderRankingList()}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default withUser(SignedPercentage);
